import React, { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Dropdown, Modal } from "react-bootstrap";
import {
  getSemesters,
  createSemester,
  updateSemester,
  deleteSemester,
} from "../../../services/SemesterService";
import { getAcademicYears } from "../../../services/AcademicYearService";
import DataTablePagination from "../common/DataTablePagination";
import { Trans, useTranslation } from "react-i18next";
import { usePermissions, PERMISSIONS } from "../../../utils/permissions";

const emptyForm = {
  academic_year_id: "",
  code: "",
  name: "",
  semester_order: 1,
  start_date: "",
  end_date: "",
  is_active: true,
};

const Semesters = () => {
  const { t } = useTranslation();
  const can = usePermissions();
  const canCreate = can(PERMISSIONS.SEMESTER_CREATE);
  const canUpdate = can(PERMISSIONS.SEMESTER_UPDATE);
  const canDelete = can(PERMISSIONS.SEMESTER_DELETE);
  const [semesters, setSemesters] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState("");

  const [academicYears, setAcademicYears] = useState([]);

  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(10);
  const [total, setTotal] = useState(0);
  const [totalPages, setTotalPages] = useState(1);

  const [searchInput, setSearchInput] = useState("");
  const [search, setSearch] = useState("");
  const [yearFilter, setYearFilter] = useState("");
  const [sortBy, setSortBy] = useState("semester_order");
  const [sortDir, setSortDir] = useState("asc");

  const [showFormModal, setShowFormModal] = useState(false);
  const [formMode, setFormMode] = useState("add");
  const [formData, setFormData] = useState(emptyForm);
  const [formErrors, setFormErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState("");
  const [editingId, setEditingId] = useState(null);

  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [feedback, setFeedback] = useState(null);

  useEffect(() => {
    getAcademicYears({ page: 1, limit: 100 })
      .then((res) => setAcademicYears(res.data?.data || []))
      .catch(() => {});
  }, []);

  useEffect(() => {
    const timer = setTimeout(() => {
      setSearch(searchInput.trim());
      setPage(1);
    }, 400);
    return () => clearTimeout(timer);
  }, [searchInput]);

  const fetchSemesters = useCallback(async () => {
    setLoading(true);
    setLoadError("");
    try {
      const params = { page, limit, sort_by: sortBy, sort_dir: sortDir };
      if (search) params.search = search;
      if (yearFilter) params.academic_year_id = yearFilter;

      const res = await getSemesters(params);
      const body = res.data;
      setSemesters(body.data || []);
      setTotal(body.pagination?.total ?? 0);
      setTotalPages(body.pagination?.totalPages ?? 1);
    } catch (err) {
      setLoadError(err?.response?.data?.message || t("pages.semesters.impossibleChargerSemestres"));
      setSemesters([]);
    } finally {
      setLoading(false);
    }
  }, [page, limit, sortBy, sortDir, search, yearFilter, t]);

  useEffect(() => {
    fetchSemesters();
  }, [fetchSemesters]);

  useEffect(() => {
    if (!feedback) return undefined;
    const timer = setTimeout(() => setFeedback(null), 4000);
    return () => clearTimeout(timer);
  }, [feedback]);

  const handleSort = (field) => {
    if (sortBy === field) setSortDir((prev) => (prev === "asc" ? "desc" : "asc"));
    else {
      setSortBy(field);
      setSortDir("asc");
    }
  };
  const getSortIcon = (field) => (sortBy !== field ? "↕" : sortDir === "asc" ? "↑" : "↓");

  const resetFilters = () => {
    setSearchInput("");
    setSearch("");
    setYearFilter("");
    setSortBy("semester_order");
    setSortDir("asc");
    setPage(1);
  };

  const getYearLabel = (id) => academicYears.find((y) => y.id === id)?.name || "-";

  const openAddModal = () => {
    setFormMode("add");
    setFormData(emptyForm);
    setFormErrors({});
    setSubmitError("");
    setEditingId(null);
    setShowFormModal(true);
  };

  const openEditModal = (semester) => {
    setFormMode("edit");
    setFormData({
      academic_year_id: semester.academic_year_id || "",
      code: semester.code || "",
      name: semester.name || "",
      semester_order: semester.semester_order || 1,
      start_date: semester.start_date ? semester.start_date.slice(0, 10) : "",
      end_date: semester.end_date ? semester.end_date.slice(0, 10) : "",
      is_active: !!semester.is_active,
    });
    setFormErrors({});
    setSubmitError("");
    setEditingId(semester.id);
    setShowFormModal(true);
  };

  const closeFormModal = () => {
    if (submitting) return;
    setShowFormModal(false);
  };

  const handleFormChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    setFormErrors((prev) => ({ ...prev, [field]: undefined }));
  };

  const validateForm = () => {
    const errors = {};
    if (!formData.academic_year_id) errors.academic_year_id = t("common.ui.lAnneeUniversitaireEstRequise");
    if (!formData.code.trim()) errors.code = t("common.ui.leCodeEstRequis");
    if (!formData.name.trim()) errors.name = t("common.ui.leNomEstRequis");
    if (!formData.semester_order || formData.semester_order < 1)
      errors.semester_order = t("pages.semesters.orderMin");
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;
    setSubmitting(true);
    setSubmitError("");
    try {
      const payload = {
        academic_year_id: formData.academic_year_id,
        code: formData.code.trim(),
        name: formData.name.trim(),
        semester_order: Number(formData.semester_order),
        start_date: formData.start_date || null,
        end_date: formData.end_date || null,
        is_active: formData.is_active,
      };
      if (formMode === "add") {
        await createSemester(payload);
        setFeedback({ type: "success", message: t("pages.semesters.semestreCreeAvecSucces") });
      } else {
        await updateSemester(editingId, payload);
        setFeedback({ type: "success", message: t("pages.semesters.semestreMisJour") });
      }
      setShowFormModal(false);
      fetchSemesters();
    } catch (err) {
      setSubmitError(err?.response?.data?.message || t("pages.semesters.uneErreurEstSurvenue"));
    } finally {
      setSubmitting(false);
    }
  };

  const confirmDelete = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      await deleteSemester(deleteTarget.id);
      setFeedback({ type: "success", message: t("pages.semesters.semestreSupprime") });
      setDeleteTarget(null);
      if (semesters.length === 1 && page > 1) setPage((p) => p - 1);
      else fetchSemesters();
    } catch (err) {
      setFeedback({
        type: "danger",
        message: err?.response?.data?.message || t("pages.semesters.impossibleSupprimerSemestre"),
      });
      setDeleteTarget(null);
    } finally {
      setDeleting(false);
    }
  };

  const toggleActive = async (semester) => {
    try {
      await updateSemester(semester.id, { is_active: !semester.is_active });
      setSemesters((prev) =>
        prev.map((item) => (item.id === semester.id ? { ...item, is_active: !semester.is_active } : item)),
      );
    } catch (err) {
      setFeedback({ type: "danger", message: t("common.crud.toggleError") });
    }
  };

  return (
    <div className="page-wrapper">
      <div className="content">
        <div className="d-md-flex d-block align-items-center justify-content-between mb-3">
          <div className="my-auto mb-2">
            <nav>
              <ol className="breadcrumb mb-0">
                <li className="breadcrumb-item">
                  <Link to="/dashboard">{t("menu.dashboard.title")}</Link>
                </li>
                <li className="breadcrumb-item active">{t("menu.semesters.manage")}</li>
              </ol>
            </nav>
          </div>
          <div className="d-flex my-xl-auto right-content align-items-center flex-wrap">
            <div className="pe-1 mb-2">
              <button type="button" className="btn btn-outline-light bg-white btn-icon me-1" onClick={resetFilters}>
                <i className="ti ti-refresh"></i>
              </button>
            </div>
            <div className="mb-2">
              {canCreate && (
<button type="button" className="btn btn-primary d-flex align-items-center" onClick={openAddModal}>
                <i className="ti ti-square-rounded-plus me-2"></i>{t("pages.semesters.addLabel")}
              </button>
)}
            </div>
          </div>
        </div>

        {feedback && (
          <div className={`alert alert-${feedback.type} alert-dismissible fade show`} role="alert">
            {feedback.message}
            <button type="button" className="btn-close" onClick={() => setFeedback(null)}></button>
          </div>
        )}

        <div className="card">
          <div className="card-header d-flex align-items-center justify-content-between flex-wrap pb-0">
            <h4 className="mb-3">{t("pages.semesters.title")}</h4>
            <div className="d-flex align-items-center flex-wrap">
              <div className="input-icon-start mb-3 me-2 position-relative">
                <span className="icon-addon"></span>
                <input
                  type="text"
                  className="form-control"
                  placeholder={t("common.ui.rechercherCodeNom")}
                  value={searchInput}
                  onChange={(e) => setSearchInput(e.target.value)}
                  style={{ paddingLeft: "35px", minWidth: "220px" }}
                />
              </div>
              <div className="mb-3 me-2">
                <select
                  className="form-select"
                  value={yearFilter}
                  onChange={(e) => {
                    setYearFilter(e.target.value);
                    setPage(1);
                  }}
                >
                  <option value="">{t("common.fields.allYears")}</option>
                  {academicYears.map((y) => (
                    <option key={y.id} value={y.id}>
                      {y.name}
                    </option>
                  ))}
                </select>
              </div>
            </div>
          </div>

          <DataTablePagination
            variant="header"
            page={page}
            limit={limit}
            total={total}
            totalPages={totalPages}
            onPageChange={setPage}
            onLimitChange={(v) => {
              setLimit(v);
              setPage(1);
            }}
          />

          <div className="card-body p-0 py-3">
            <div className="custom-datatable-filter table-responsive">
              <table className="table datatable">
                <thead className="thead-light">
                  <tr>
                    <th onClick={() => handleSort("semester_order")} style={{ cursor: "pointer" }}>
                      {t("common.fields.order")} {getSortIcon("semester_order")}
                    </th>
                    <th onClick={() => handleSort("code")} style={{ cursor: "pointer" }}>
                      {t("common.fields.code")} {getSortIcon("code")}
                    </th>
                    <th onClick={() => handleSort("name")} style={{ cursor: "pointer" }}>
                      {t("common.fields.name")} {getSortIcon("name")}
                    </th>
                    <th>{t("common.fields.academicYear")}</th>
                    <th>{t("common.fields.period")}</th>
                    <th>{t("common.crud.statusColumn")}</th>
                    <th>{t("common.crud.actionColumn")}</th>
                  </tr>
                </thead>
                <tbody>
                  {loading ? (
                    <tr>
                      <td colSpan="7" className="text-center py-4">
                        <div className="spinner-border text-primary" role="status">
                          <span className="visually-hidden">{t("common.crud.loading")}</span>
                        </div>
                      </td>
                    </tr>
                  ) : loadError ? (
                    <tr>
                      <td colSpan="7" className="text-center py-4 text-danger">
                        {loadError}
                      </td>
                    </tr>
                  ) : semesters.length > 0 ? (
                    semesters.map((semester) => (
                      <tr key={semester.id}>
                        <td>{semester.semester_order}</td>
                        <td className="fw-medium">{semester.code}</td>
                        <td>{semester.name}</td>
                        <td>{getYearLabel(semester.academic_year_id)}</td>
                        <td>
                          {semester.start_date ? semester.start_date.slice(0, 10) : "-"} →{" "}
                          {semester.end_date ? semester.end_date.slice(0, 10) : "-"}
                        </td>
                        <td>
                          <span
                            className={`badge badge-soft-${semester.is_active ? "success" : "danger"} d-inline-flex align-items-center`}
                            role="button"
                            onClick={() => toggleActive(semester)}
                          >
                            <i className="ti ti-circle-filled fs-5 me-1"></i>
                            {semester.is_active ? t("common.crud.statusActive") : t("common.crud.statusInactive")}
                          </span>
                        </td>
                        <td>
                          <Dropdown>
                            <Dropdown.Toggle
                              as="button"
                              className="btn btn-white btn-icon btn-sm d-flex align-items-center justify-content-center rounded-circle p-0 i-false"
                            >
                              <i className="ti ti-dots-vertical fs-14"></i>
                            </Dropdown.Toggle>
                            <Dropdown.Menu align="end" className="p-3">
                              {canUpdate && (
<Dropdown.Item className="rounded-1" onClick={() => openEditModal(semester)}>
                                <i className="ti ti-edit-circle me-2"></i>{t("common.crud.edit")}
                              </Dropdown.Item>
)}
                              <Dropdown.Item className="rounded-1" onClick={() => toggleActive(semester)}>
                                <i className="ti ti-toggle-right me-2"></i>
                                {semester.is_active ? t("common.crud.deactivate") : t("common.crud.activate")}
                              </Dropdown.Item>
                              {canDelete && (
<Dropdown.Item className="rounded-1" onClick={() => setDeleteTarget(semester)}>
                                <i className="ti ti-trash-x me-2"></i>{t("common.crud.delete")}
                              </Dropdown.Item>
)}
                            </Dropdown.Menu>
                          </Dropdown>
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan="7" className="text-center py-4">
                        <i className="ti ti-search-off fs-1 text-muted"></i>
                        <h5 className="mt-2">{t("pages.semesters.aucunSemestreTrouve")}</h5>
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>

            <DataTablePagination
              variant="footer"
              page={page}
              limit={limit}
              total={total}
              totalPages={totalPages}
              onPageChange={setPage}
              onLimitChange={(v) => {
                setLimit(v);
                setPage(1);
              }}
            />
          </div>
        </div>
      </div>

      <Modal show={showFormModal} onHide={closeFormModal} centered>
        <form onSubmit={handleSubmit}>
          <Modal.Header closeButton>
            <Modal.Title>{formMode === "add" ? t("pages.semesters.addLabel") : t("pages.semesters.editTitle")}</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            {submitError && <div className="alert alert-danger">{submitError}</div>}
            <div className="mb-3">
              <label className="form-label">
                {t("common.fields.academicYear")} <span className="text-danger">*</span>
              </label>
              <select
                className={`form-select ${formErrors.academic_year_id ? "is-invalid" : ""}`}
                value={formData.academic_year_id}
                onChange={(e) => handleFormChange("academic_year_id", e.target.value)}
              >
                <option value="">{t("common.crud.selectPlaceholder")}</option>
                {academicYears.map((y) => (
                  <option key={y.id} value={y.id}>
                    {y.name}
                  </option>
                ))}
              </select>
              {formErrors.academic_year_id && <div className="invalid-feedback">{formErrors.academic_year_id}</div>}
            </div>
            <div className="row">
              <div className="col-md-6 mb-3">
                <label className="form-label">
                  {t("common.fields.code")} <span className="text-danger">*</span>
                </label>
                <input
                  type="text"
                  className={`form-control ${formErrors.code ? "is-invalid" : ""}`}
                  placeholder="S1"
                  value={formData.code}
                  onChange={(e) => handleFormChange("code", e.target.value)}
                />
                {formErrors.code && <div className="invalid-feedback">{formErrors.code}</div>}
              </div>
              <div className="col-md-6 mb-3">
                <label className="form-label">
                  {t("common.fields.order")} <span className="text-danger">*</span>
                </label>
                <input
                  type="number"
                  min={1}
                  className={`form-control ${formErrors.semester_order ? "is-invalid" : ""}`}
                  value={formData.semester_order}
                  onChange={(e) => handleFormChange("semester_order", e.target.value)}
                />
                {formErrors.semester_order && <div className="invalid-feedback">{formErrors.semester_order}</div>}
              </div>
            </div>
            <div className="mb-3">
              <label className="form-label">
                {t("common.fields.name")} <span className="text-danger">*</span>
              </label>
              <input
                type="text"
                className={`form-control ${formErrors.name ? "is-invalid" : ""}`}
                placeholder={t("pages.semesters.semestre1")}
                value={formData.name}
                onChange={(e) => handleFormChange("name", e.target.value)}
              />
              {formErrors.name && <div className="invalid-feedback">{formErrors.name}</div>}
            </div>
            <div className="row">
              <div className="col-md-6 mb-3">
                <label className="form-label">{t("common.fields.startDate")}</label>
                <input
                  type="date"
                  className="form-control"
                  value={formData.start_date}
                  onChange={(e) => handleFormChange("start_date", e.target.value)}
                />
              </div>
              <div className="col-md-6 mb-3">
                <label className="form-label">{t("common.fields.endDate")}</label>
                <input
                  type="date"
                  className="form-control"
                  value={formData.end_date}
                  onChange={(e) => handleFormChange("end_date", e.target.value)}
                />
              </div>
            </div>
            <div className="form-check form-switch">
              <input
                className="form-check-input"
                type="checkbox"
                role="switch"
                id="semester-is-active"
                checked={formData.is_active}
                onChange={(e) => handleFormChange("is_active", e.target.checked)}
              />
              <label className="form-check-label" htmlFor="semester-is-active">
                {t("pages.semesters.activeLabel")}
              </label>
            </div>
          </Modal.Body>
          <Modal.Footer>
            <button type="button" className="btn btn-light" onClick={closeFormModal} disabled={submitting}>
              {t("common.crud.cancel")}
            </button>
            <button type="submit" className="btn btn-primary" disabled={submitting}>
              {submitting ? t("common.crud.saving") : formMode === "add" ? t("common.crud.add") : t("common.crud.save")}
            </button>
          </Modal.Footer>
        </form>
      </Modal>

      <Modal show={deleteTarget !== null} onHide={() => (deleting ? null : setDeleteTarget(null))} centered>
        <div className="modal-body text-center">
          <span className="delete-icon">
            <i className="ti ti-trash-x"></i>
          </span>
          <h4>{t("common.crud.confirmDeleteTitle")}</h4>
          <p>
            <Trans i18nKey="pages.semesters.voulezVousVraimentSupprimerSemestre" values={{ name: deleteTarget?.name }} components={[<strong />]} />
          </p>
          <div className="d-flex justify-content-center">
            <button type="button" className="btn btn-light me-3" onClick={() => setDeleteTarget(null)} disabled={deleting}>
              {t("common.crud.cancel")}
            </button>
            <button type="button" className="btn btn-danger" onClick={confirmDelete} disabled={deleting}>
              {deleting ? t("common.crud.deleting") : t("common.crud.confirmDelete")}
            </button>
          </div>
        </div>
      </Modal>
    </div>
  );
};

export default Semesters;
